import React from 'react';
import styled, { css } from 'styled-components';
import { switchProp } from 'styled-tools';
import { Link as ReactRouterLink } from 'react-router-dom';
import { media, layout, styledUtils } from 'src/components/base';
import {
  ProductCheckbox,
  PRODUCT_CHECKBOX_SIZE,
  productCheckboxHoverStyle,
} from 'src/components/product-checkbox';

// We want to exclude the hasExtra prop from forwarding, as it would otherwise be set on Link's DOM
// element, which triggers a React warning.
const Link = ({
  hasExtra,
  ...props
}: { hasExtra?: boolean } & React.ComponentProps<typeof ReactRouterLink>) => (
  <ReactRouterLink {...props} />
);

type TableRowProps = { selectable?: boolean; selected?: boolean };
export const TableRow = styled.div.attrs({
  'data-test': 'ProductListRow',
})<TableRowProps>`
  position: relative;
  display: table-row;
  border-bottom: 1px solid ${p => p.theme.colors.base200};
  ${media.mediumDown`
    display: flex;
    align-items: center;
  `};
`;

type LinkCellProps = { selected?: boolean };
export const LinkCell = styled(Link)<LinkCellProps>`
  display: table-cell;
  position: relative;
  vertical-align: middle;

  ${props =>
    props.selected &&
    css`
      &::before {
        ${styledUtils.overlay(props.theme.bg.productCompareLight)}
      }
    `}

  ${/* sc-selector */ TableRow}:hover &::after,
  ${/* sc-selector */ TableRow}:focus-within &::after {
    ${styledUtils.overlay()}
  }
`;

export const ImageWrapper = styled(LinkCell)`
  order: 0;
  width: 40px;
  text-align: center;
  height: auto;
  padding: 10px 5px;
`;

export const Image = styled.div`
  background: ${p => p.theme.colors.base200};
  border-radius: ${layout.borderRadius};
  width: 40px;
  height: 40px;

  img {
    display: block;
    max-width: 40px;
    max-height: 40px;
    width: auto;
    height: auto;
  }
`;

export const BasicInfo = styled(LinkCell)`
  flex: 1;
  min-width: 200px;
  position: relative;
  padding: 0px ${props => (props.hasExtra ? '70px' : '5px')} 0 5px;
  ${media.largeUp`
    max-width: 230px;
  `};
`;

export const ProductDetails = styled.div`
  display: inline-block;
`;

export const ProductName = styled.div.attrs({ 'data-test': 'ProductName' })`
  margin: 5px 0px 0px 0px;
`;

export const Subtitle = styled.div`
  color: ${p => p.theme.colors.base500};
  font-size: 12px;
`;

export const BadgeArea = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  bottom: 10px;
  display: flex;
  align-items: center;
  flex-direction: column-reverse;
  justify-content: space-around;
  width: 25%;
`;

export const CampaignBadge = styled.span`
  display: block;
  padding: 5px;
  max-height: 55px;
  max-width: 55px;
`;

export const PriceLabelWrapper = styled(LinkCell)`
  outline: none;
  flex: 0 0 20%;
  text-align: right;
  order: 2;
  padding-right: 20px;
`;

export const StoreCount = styled.span`
  display: flex;
  justify-content: flex-end;
  font-size: 12px;
  color: ${p => p.theme.text.tertiary};
  fill: ${p => p.theme.icons.tertiary};
  align-items: center;

  svg {
    height: 16px !important;
    width: 16px !important;
    margin-left: 2px;
  }
`;

export const PropertyCell = styled(LinkCell)`
  outline: none;
  font-size: 12px;
  padding-right: 8px;
  color: ${p => p.theme.text.tertiary};
  width: ${switchProp(
    'width',
    {
      narrow: '160px',
      narrower: '120px',
      narrowest: '100px',
    },
    'auto'
  )};
  ${media.mediumDown`
    display: none;
  `};
`;

type SelectBoxCellProps = { selected?: boolean };
export const SelectBoxCell = styled.label<SelectBoxCellProps>`
  cursor: pointer;
  display: table-cell;
  position: relative;
  padding: 0 ${layout.measurements.spacing050};
  vertical-align: middle;
  width: ${PRODUCT_CHECKBOX_SIZE}px;

  ${props =>
    props.selected &&
    css`
      &::before {
        ${styledUtils.overlay(props.theme.bg.productCompareLight)}
      }
    `}

  ${/* sc-selector */ TableRow}:hover &::after,
  ${/* sc-selector */ TableRow}:focus-within &::after {
    ${styledUtils.overlay()}
  }
`;

export const SelectBox = styled(ProductCheckbox)`
  box-sizing: border-box;
  margin: 10px 2px;
  width: ${PRODUCT_CHECKBOX_SIZE}px;

  ${/* sc-selector */ TableRow}:hover & {
    ${productCheckboxHoverStyle}
  }
`;
